import type { DashboardLayoutType } from '@/api/dashboardsApi'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { type ElementSize, SkeletonRegion, fitCount, useElementSize } from './_shared'
import { ChartSkeleton } from './chart-skeleton'
import { DetailSkeleton } from './detail-skeleton'
import { DevicePulseSkeleton } from './device-pulse-skeleton'
import { IframeSkeleton } from './iframe-skeleton'
import { LedSkeleton } from './led-skeleton'
import { LineSkeleton } from './line-skeleton'
import { LogsSkeleton } from './logs-skeleton'
import { MapSkeleton } from './map-skeleton'
import { StateTimelineSkeleton } from './state-timeline-skeleton'
import { StaticTextSkeleton } from './static-text-skeleton'
import { TableSkeleton } from './table-skeleton'

export interface PanelSkeletonProps {
	panelType?: string
	body?: React.ReactNode
	withHeader?: boolean
	className?: string
	label?: string | false
	dashboardType?: DashboardLayoutType
}

// Bone footprints used to fit counts to the measured panel body.
const TABLE_ROW_HEIGHT = 36.5
const TABLE_COL_WIDTH = 140
const DETAIL_LINE_HEIGHT = 28
const PULSE_ITEM_HEIGHT = 37

interface PanelBodyOptions {
	panelType?: string
	dashboardType?: DashboardLayoutType
	size: ElementSize
}

function bodyForPanelType({ panelType, dashboardType, size }: PanelBodyOptions): React.ReactNode {
	const t = (panelType ?? '').toLowerCase()
	if (
		t.includes('locate') ||
		t.includes('track') ||
		t.includes('map') ||
		t === 'locate_devices' ||
		t === 'track_devices' ||
		t === 'micelio_locate_devices' ||
		t === 'micelio_track_devices'
	) {
		return <MapSkeleton label={false} />
	}
	if (
		t.includes('table') ||
		t.includes('aggregate') ||
		t.includes('session') ||
		t.includes('last') ||
		t.includes('value')
	) {
		return (
			<TableSkeleton
				// -1 row for the header, which the table renders on top of the body rows.
				rows={Math.max(1, fitCount(size.height, TABLE_ROW_HEIGHT, 2, 31) - 1)}
				cols={fitCount(size.width, TABLE_COL_WIDTH, 2, 8)}
				label={false}
				className='size-full'
			/>
		)
	}
	if (t.includes('gauge')) {
		return <ChartSkeleton variant='gauge' height='h-full' label={false} />
	}
	if (t.includes('donut') || t.includes('pie')) {
		return <ChartSkeleton variant='donut' height='h-full' withLegend label={false} />
	}
	if (t.includes('timeline') || t === 'state_timeline') {
		return <StateTimelineSkeleton label={false} />
	}
	if (t.includes('line')) {
		return <LineSkeleton label={false} />
	}
	if (t.includes('histogram') || t.includes('bar') || t.includes('chart') || t.includes('timeseries')) {
		return <ChartSkeleton variant='bars' height='h-full' label={false} />
	}

	if (t.includes('pulse')) {
		return <DevicePulseSkeleton thresholds={fitCount(size.height, PULSE_ITEM_HEIGHT, 1, 8)} label={false} />
	}
	if (t.includes('led') || t === 'stream_status' || t.includes('stream_status')) {
		return <LedSkeleton label={false} dashboardType={dashboardType} />
	}
	if (t.includes('log')) {
		return <LogsSkeleton label={false} />
	}
	if (t.includes('status')) {
		return (
			<DetailSkeleton
				lines={fitCount(size.height, DETAIL_LINE_HEIGHT, 2, 12)}
				label={false}
				className='size-full overflow-hidden'
			/>
		)
	}
	if (t === 'static_text' || t.includes('static') || t.includes('text')) {
		return <StaticTextSkeleton label={false} />
	}
	if (t === 'iframe_panel' || t.includes('iframe')) {
		return <IframeSkeleton label={false} />
	}
	return <ChartSkeleton variant='rect' height='h-full' label={false} />
}

export function PanelSkeleton({
	panelType,
	body,
	withHeader = true,
	className,
	label = 'Loading panel',
	dashboardType,
}: PanelSkeletonProps) {
	const [bodyRef, size] = useElementSize<HTMLDivElement>()

	return (
		<SkeletonRegion label={label} className={cn('flex size-full min-h-0 flex-col gap-3 p-3', className)}>
			{withHeader ? (
				<div className='flex shrink-0 items-center justify-between'>
					<Skeleton className='h-5 w-40' />
					<div className='flex gap-2'>
						<Skeleton className='size-6 rounded-md' />
						<Skeleton className='size-6 rounded-md' />
					</div>
				</div>
			) : null}
			<div ref={bodyRef} className='min-h-0 min-w-0 flex-1 overflow-hidden'>
				{body ?? bodyForPanelType({ panelType, dashboardType, size })}
			</div>
		</SkeletonRegion>
	)
}
